import {
  useContext, useLayoutEffect, useRef, useCallback, useState,
} from 'react';
import SiteSeeContext from './SiteSeeContext';

/**
 * Hook to measure an element and store its dimensions in the SiteSee context.
 * Returns a callback ref that should be attached to the element to measure.
 *
 * @param {Object} options
 * @param {string} options.id - The id to store dimensions under
 * @param {string} options.idAlias - Alternative id that maps to a standard id
 * @returns {Function} Callback ref to attach to the target element
 */
const useSiteSeeModalDims = ({ id, idAlias }) => {
  // Always call hooks unconditionally (Rules of Hooks)
  const {
    setSiteSeeModalDims,
  } = useContext(SiteSeeContext);

  // Track the element via state so we re-render when it mounts/unmounts
  const [element, setElement] = useState(null);

  // Resolve the actual id from idAlias
  let resolvedId = id;
  if (!resolvedId && idAlias) {
    switch (idAlias) {
      case 'mapLevelsNav':
        resolvedId = 'levels';
        break;
      case 'residenceFilter':
        resolvedId = 'filters';
        break;
      default:
        break;
    }
  }

  // Store resolvedId in a ref to avoid stale closures
  const resolvedIdRef = useRef(resolvedId);
  resolvedIdRef.current = resolvedId;

  // Callback ref - this gets called when element mounts/unmounts
  const callbackRef = useCallback((node) => {
    setElement(node);
  }, []);

  // Memoize the measure function
  const measureAndUpdate = useCallback(() => {
    const currentId = resolvedIdRef.current;
    if (!currentId || !element || !setSiteSeeModalDims) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const dim = {
      width: rect.width,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
    };

    setSiteSeeModalDims((prevDims) => {
      const previousSelfDim = prevDims?.find((item) => item.id === currentId);

      // Only update if values actually changed
      if (
        previousSelfDim
        && previousSelfDim.width === dim.width
        && previousSelfDim.top === dim.top
        && previousSelfDim.right === dim.right
        && previousSelfDim.bottom === dim.bottom
        && previousSelfDim.left === dim.left
      ) {
        return prevDims;
      }

      // Remove old entry for this id and add new one
      const filtered = prevDims?.filter((item) => item.id !== currentId) || [];
      return [...filtered, { id: currentId, ...dim }];
    });
  }, [element, setSiteSeeModalDims]);

  // useLayoutEffect runs synchronously after DOM mutations but before paint
  useLayoutEffect(() => {
    if (!resolvedIdRef.current || !element || !setSiteSeeModalDims) {
      return undefined;
    }

    // Measure after a microtask to ensure styles are applied
    let rafId = requestAnimationFrame(() => {
      measureAndUpdate();
    });

    // Defer to next frame to avoid
    // "ResizeObserver loop completed with undelivered notifications" error
    const resizeObserver = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        measureAndUpdate();
      });
    });

    resizeObserver.observe(element);

    // Listen for window resize and scroll (captures position changes)
    window.addEventListener('resize', measureAndUpdate);
    window.addEventListener('scroll', measureAndUpdate, true);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureAndUpdate);
      window.removeEventListener('scroll', measureAndUpdate, true);
    };
  }, [element, setSiteSeeModalDims, measureAndUpdate]);

  // Return the callback ref for the consumer to attach
  return callbackRef;
};

export default useSiteSeeModalDims;
